import { useEffect, useRef, useState } from 'react';
import {
  clearSupportAgentSession,
  requestSupportReply,
} from './request-support-reply';
import { ChatMessage } from './support-agent-types';

const INITIAL_MESSAGES: ChatMessage[] = [
  {
    id: 'welcome',
    role: 'assistant',
    content: 'Hi, how can I help you with gPanel today?',
    status: 'complete',
  },
];

export function useSupportAgentChat() {
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [isSending, setIsSending] = useState(false);
  const messagesRef = useRef(messages);
  const isSendingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const updateMessage = (id: string, patch: Partial<ChatMessage>) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === id ? { ...message, ...patch } : message
      )
    );
  };

  const sendPrompt = async (prompt: string, assistantMessageId: string) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    isSendingRef.current = true;
    setIsSending(true);

    try {
      const reply = await requestSupportReply(prompt, controller.signal);
      updateMessage(assistantMessageId, {
        content: reply.reply,
        sources: reply.sources,
        status: 'complete',
        errorMessage: undefined,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Drop aborted pending bubbles so they never stick as "Thinking…"
        setMessages((prev) =>
          prev.filter((message) => message.id !== assistantMessageId)
        );
        return;
      }

      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Something went wrong. Please try again.';

      updateMessage(assistantMessageId, {
        content: '',
        status: 'error',
        errorMessage,
        sources: undefined,
      });
    } finally {
      if (abortControllerRef.current === controller) {
        isSendingRef.current = false;
        setIsSending(false);
        abortControllerRef.current = null;
      }
    }
  };

  const send = async (rawContent: string) => {
    const content = rawContent.trim();
    // Sync re-entry guard — React state alone can miss same-tick double submits.
    if (!content || isSendingRef.current) {
      return;
    }

    isSendingRef.current = true;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      status: 'complete',
    };

    const assistantMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      status: 'pending',
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    await sendPrompt(content, assistantMessage.id);
  };

  const retry = async (failedAssistantId: string) => {
    if (isSendingRef.current) {
      return;
    }

    const currentMessages = messagesRef.current;
    const failedIndex = currentMessages.findIndex(
      (message) => message.id === failedAssistantId
    );
    if (failedIndex <= 0) {
      return;
    }

    const priorUserMessage = [...currentMessages]
      .slice(0, failedIndex)
      .reverse()
      .find(
        (message) => message.role === 'user' && message.status === 'complete'
      );

    if (!priorUserMessage) {
      return;
    }

    isSendingRef.current = true;

    updateMessage(failedAssistantId, {
      content: '',
      status: 'pending',
      errorMessage: undefined,
      sources: undefined,
    });

    await sendPrompt(priorUserMessage.content, failedAssistantId);
  };

  const resetConversation = async () => {
    if (isSendingRef.current) {
      return;
    }

    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    isSendingRef.current = true;
    setIsSending(true);

    try {
      await clearSupportAgentSession();
      setMessages(INITIAL_MESSAGES);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      // Keep local transcript; surface failure as a transient assistant error bubble.
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Could not start a new conversation. Please try again.';

      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: '',
          status: 'error',
          errorMessage,
        },
      ]);
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  };

  return {
    messages,
    isSending,
    send,
    retry,
    resetConversation,
  };
}
